import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { apiClient, type ApiResponse } from '@/lib/api-client';

/**
 * Customer ID generation configuration.
 * - usePrefix:     when on, the entity type's prefix is prepended (PREFIX-NNNNN);
 *                  when off, codes are just the padded number (NNNNN).
 * - padWidth:      zero-pad width of the running number (e.g. 5 → 00001)
 * - nextNumber:    the next number to be generated
 * - nextCode:      server-computed preview of the next full code
 * - examplePrefix: the entity-type prefix used in the preview (when usePrefix)
 */
interface CustomerIdConfig {
  usePrefix: boolean;
  padWidth: number;
  nextNumber: number;
  nextCode: string;
  examplePrefix: string | null;
}

/** Builds a live preview code from the in-progress form values. */
function buildPreview(
  usePrefix: boolean,
  examplePrefix: string | null,
  padWidth: number,
  nextNumber: number,
): string {
  const safeWidth = Math.min(10, Math.max(1, Math.floor(padWidth) || 1));
  const safeNumber = Math.max(1, Math.floor(nextNumber) || 1);
  const padded = String(safeNumber).padStart(safeWidth, '0');
  const prefix = usePrefix ? (examplePrefix || 'CUST') : null;
  return prefix ? `${prefix}-${padded}` : padded;
}

/**
 * CustomerIdSettings — configure how new customer (contact) codes are numbered.
 *
 * The number always comes from these settings (per entity type). The prefix is
 * optional: when the toggle is on, the contact's entity type prefix is prepended
 * (e.g. CUST-00001); when off, codes are just the padded number (e.g. 00001).
 * The next code is previewed live using the first entity type as an example.
 */
export function CustomerIdSettings() {
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery({
    queryKey: ['settings', 'customer-id'],
    queryFn: async () => {
      const response =
        await apiClient.get<ApiResponse<CustomerIdConfig>>('/settings/customer-id');
      return response.data.data;
    },
  });

  const [form, setForm] = useState({ usePrefix: true, padWidth: 5, nextNumber: 1 });
  const [initialized, setInitialized] = useState(false);
  if (config && !initialized) {
    setForm({
      usePrefix: config.usePrefix,
      padWidth: config.padWidth,
      nextNumber: config.nextNumber,
    });
    setInitialized(true);
  }

  const updateMutation = useMutation({
    mutationFn: async (data: { usePrefix: boolean; padWidth: number; nextNumber: number }) => {
      const response = await apiClient.put<ApiResponse<CustomerIdConfig>>(
        '/settings/customer-id',
        data,
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['settings', 'customer-id'], data);
      setForm({ usePrefix: data.usePrefix, padWidth: data.padWidth, nextNumber: data.nextNumber });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({
      usePrefix: form.usePrefix,
      padWidth: form.padWidth,
      nextNumber: form.nextNumber,
    });
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
        </div>
      </Card>
    );
  }

  // Live preview from current (unsaved) form values. The example prefix comes
  // from the server (first entity type); fall back to CUST before first load.
  const examplePrefix = config?.examplePrefix ?? 'CUST';
  const livePreview = buildPreview(form.usePrefix, examplePrefix, form.padWidth, form.nextNumber);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader
          title="Customer ID"
          description="Control how new customer codes are numbered. The next code is shown below and updates as you change these options."
        />
        <CardContent>
          {/* Prefix toggle */}
          <div className="flex items-start justify-between gap-4 border-b border-border pb-4 dark:border-border-dark">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Use entity type prefix
              </p>
              <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                On: codes look like <span className="font-mono">{examplePrefix}-00001</span> (prefix
                from the contact&apos;s entity type). Off: codes are just the number, e.g.{' '}
                <span className="font-mono">00001</span>.
              </p>
            </div>
            <label className="relative mt-1 inline-flex shrink-0 cursor-pointer items-center">
              <input
                type="checkbox"
                checked={form.usePrefix}
                onChange={(e) => setForm((prev) => ({ ...prev, usePrefix: e.target.checked }))}
                className="peer sr-only"
                role="switch"
                aria-label="Use entity type prefix"
              />
              <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-600 peer-checked:after:translate-x-full peer-focus:ring-2 peer-focus:ring-brand-500 peer-focus:ring-offset-1 dark:bg-gray-600 dark:peer-checked:bg-brand-500" />
            </label>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Number padding"
              type="number"
              min={1}
              max={10}
              value={form.padWidth}
              onChange={(e) => setForm((prev) => ({ ...prev, padWidth: Number(e.target.value) }))}
              required
              hint="Zero-pad width (5 → 00001)"
            />
            <Input
              label="Next number"
              type="number"
              min={1}
              value={form.nextNumber}
              onChange={(e) => setForm((prev) => ({ ...prev, nextNumber: Number(e.target.value) }))}
              required
              hint="Applied to every entity type's sequence"
            />
          </div>

          {/* Live preview of the next code to be generated */}
          <div className="mt-6 rounded-lg border border-border bg-surface-secondary p-4 dark:border-border-dark dark:bg-surface-dark-tertiary">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Next customer code</p>
            <p className="mt-1 font-mono text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {livePreview}
            </p>
            {form.usePrefix && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Example uses the <span className="font-mono">{examplePrefix}</span> entity type
                prefix. Each entity type uses its own prefix and counter.
              </p>
            )}
            {initialized && config && livePreview !== config.nextCode && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                Unsaved — currently the next code is{' '}
                <span className="font-mono">{config.nextCode}</span>. Save to apply.
              </p>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex items-center justify-end gap-3">
          {updateMutation.isError && (
            <span className="text-sm text-red-600 dark:text-red-400">
              Could not save. Check the values and try again.
            </span>
          )}
          {updateMutation.isSuccess && !updateMutation.isPending && (
            <span className="text-sm text-green-600 dark:text-green-400">Saved</span>
          )}
          <Button type="submit" loading={updateMutation.isPending}>
            Save changes
          </Button>
        </CardFooter>
      </Card>
    </form>
  );
}
